// Telemetry row builders (IMPLEMENTATION §2.9 event list; one place that fixes each event's columns so the
// report tool can read them). Multi-field details go into `extra` as compact JSON.
//   session_start  subject = layout class, value = cell CSS px, extra = {device, ua, vw, vh, dpr, dprEff,
//                  renderer, version, build, standalone}; `device` = a random id kept in the ring (telemetryHub)
//                  so the report tells two phones with the same UA and the same session numbers apart
//   session_end    subject = reason (pagehide), value = session wall seconds, extra = {tick}
//   layout         subject = layout class, value = cell CSS px, extra = {cols, rows} (on change; telemetryHub)
//   drag (M6)      subject = payload kind, value = ms, extra = {from, to, result, reason, distPx}; logged
//                  by input/router.ts only. result = the resolver outcome (merge | move | swap | insert | sell |
//                  deliver | buyPlace | buyMerge | bmove | bmerge | bswap | bsell | none = its own cell) |
//                  snapback (reason = the refusal, offBoard, an inert zone) | cancel (reason = peek, stale or
//                  the gesture's CancelReason) (UX §7); from = cell:x,y | shop:<id> | slot:<bid>,
//                  to = cell:x,y | counter | sell | peek | gunSlot | none
//   rage_tap (M6)  subject = "x,y", value = taps, extra = {x, y, spanMs, mode, tool}
//   error          subject = source (frame | window | promise | telemetry), value = 0, extra = message
// The SimEvent-derived rows (unlock, goal, discover, purchase, expand, hint_*, merge_all, stall_sec,
// gallery_mode_sec, debug) are built in telemetrySink.ts; fps rows in fpsSampler.ts / telemetryHub.ts.
import type { RageTap } from './rageTap.ts';
import type { TelemetryRow } from './telemetry.ts';

type Json = string | number | boolean | null;

/** Compact JSON for the `extra` column (CSV escaping handles its quotes and commas). */
export function extraJson(o: Readonly<Record<string, Json>>): string {
  return JSON.stringify(o);
}

export interface SessionInfo {
  ua: string;
  vw: number;
  vh: number;
  dpr: number;
  dprEff: number;
  /** Layout class (regular | compact). */
  cls: string;
  /** Cell size in CSS px. */
  cell: number;
  renderer: string;
  version: string;
  build: string;
  /** Home Screen / standalone display mode. */
  standalone: boolean;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** The session_start facts of this page (browser side). */
export function sessionInfoOf(
  win: Window,
  vp: { vw: number; vh: number; dpr: number; dprEff: number },
  layout: { cls: string; cellCss: number },
  renderer: string,
  version: string,
  build: string,
  /** platform/device.ts deviceInfo(win).standalone: the ONE standalone check (display-mode fullscreen too). */
  standalone: boolean,
): SessionInfo {
  return {
    ua: win.navigator.userAgent,
    vw: vp.vw,
    vh: vp.vh,
    dpr: vp.dpr,
    dprEff: vp.dprEff,
    cls: layout.cls,
    cell: layout.cellCss,
    renderer,
    version,
    build,
    standalone,
  };
}

export function sessionStartRow(i: SessionInfo, device?: string): TelemetryRow {
  return {
    event: 'session_start',
    subject: i.cls,
    value: round2(i.cell),
    extra: extraJson({
      ...(device !== undefined ? { device } : {}),
      ua: i.ua,
      vw: round2(i.vw),
      vh: round2(i.vh),
      dpr: round2(i.dpr),
      dprEff: round2(i.dprEff),
      renderer: i.renderer,
      version: i.version,
      build: i.build,
      standalone: i.standalone,
    }),
  };
}

export function sessionEndRow(reason: string, wallSec: number, tick: number): TelemetryRow {
  return {
    event: 'session_end',
    subject: reason,
    value: Math.round(wallSec),
    extra: extraJson({ tick }),
  };
}

export interface DragInfo {
  /** Payload kind: item family, building type or shop item. */
  payload: string;
  /** Origin, e.g. "cell:3,4" | "shop:caster" | "slot:12". */
  from: string;
  /** Target, e.g. "cell:2,3" | "counter" | "none". */
  to: string;
  /** merge | move | swap | insert | sell | deliver | buyPlace | buyMerge | … | snapback | cancel. */
  result: string;
  /** The reject reason of a snapback (RejectReason / off-board), else null. */
  reason: string | null;
  ms: number;
  distPx: number;
}

/** A finished drag (M6 logs one per drag). */
export function dragRow(d: DragInfo): TelemetryRow {
  return {
    event: 'drag',
    subject: d.payload,
    value: Math.round(d.ms),
    extra: extraJson({
      from: d.from,
      to: d.to,
      result: d.result,
      reason: d.reason,
      distPx: Math.round(d.distPx),
    }),
  };
}

/**
 * A detected rage tap (rageTap.ts; the M6 input router feeds every board tap). `ctx` = the input mode / tool
 * the taps happened in (play | coldOpen | build; move | chute | sell), appended to `extra`.
 */
export function rageTapRow(r: RageTap, ctx?: { mode: string; tool: string }): TelemetryRow {
  return {
    event: 'rage_tap',
    subject: `${r.x},${r.y}`,
    value: r.taps,
    extra: extraJson({
      x: r.x,
      y: r.y,
      spanMs: Math.round(r.spanMs),
      ...(ctx ? { mode: ctx.mode, tool: ctx.tool } : {}),
    }),
  };
}

export function errorRow(source: string, message: string): TelemetryRow {
  return { event: 'error', subject: source, value: 0, extra: message };
}
